<template>
	<span>
		<v-menu v-model="isFilterOpen" :offset="4" :close-on-content-click="false" max-width="300px" content-class="menu-content">
			<template #activator="{ props }">
				<slot name="button" :props="props">
					<v-icon
						v-if="!hasCustomButton()"
						v-test="'users-filter'"
						class="icon-filter-solid rounded-circle status-label-filter"
						v-bind="props"></v-icon>
				</slot>
			</template>
			<v-card class="search-filter" rounded="lg" elevation="8" @click.stop>
				<v-card-text class="pb-0">
					<v-text-field
						v-model="searchQuery"
						label="Search"
						append-inner-icon="icon-search-magnifying-glass"
						single-line
						variant="outlined"
						v-bind="searchFieldProps(searchQuery)">
					</v-text-field>
				</v-card-text>
				<!-- LSP-15909/LSP-16836: skeleton-while-loading + virtualized rows now live in the shared
					 FilterMenuList; the tag-specific chip row and chip-shaped skeleton stay here as slots. -->
				<FilterMenuList
					ref="containerRef"
					:items="listTags"
					:loading="loading"
					:empty-text="searchQuery.trim() ? 'No tags match your search' : 'No tags available'"
					:is-selected="isSelected"
					loading-test-id="tag-filter-loading"
					item-class="w-100 text-truncate"
					@toggle="toggleSelected">
					<template #skeleton>
						<v-skeleton-loader type="list-item-avatar" class="tag-skeleton-row"></v-skeleton-loader>
					</template>
					<template #item="{ item: tag }">
						<v-list-item-title>
							<v-chip class="text-truncate tag-chip" :color="getBackgroundColor(tag)" variant="tonal" rounded="lg">
								{{ tag.name }}
							</v-chip>
						</v-list-item-title>
					</template>
				</FilterMenuList>
				<div class="opacity-50">
					<hr />
				</div>
				<v-card-actions class="d-flex justify-end">
					<v-btn
						color="grey"
						text
						rounded="xl"
						elevation="0"
						class="white-bg"
						:disabled="loading"
						@click="clear(() => emit('filter-tags', []), true)"
						>Clear</v-btn
					>
					<v-btn
						color="primary"
						rounded="xl"
						elevation="0"
						variant="flat"
						:disabled="loading"
						@click="applyFilter(() => emit('filter-tags', [...appliedItems]))"
						>Apply</v-btn
					>
				</v-card-actions>
			</v-card>
		</v-menu>
		<span v-if="!hasCustomButton()" v-test="'table-header-users'" class="pl-1">{{ headerText }}</span>

		<!-- LSP-19045: render the applied-filter pills through the shared DisplayChip so they carry the
		     same tonal tag styling as the in-menu chips above and every other tag pill in the app. This
		     row is currently unreachable (both call sites supply a #button slot), so it is corrected
		     here to stop a third, solid tag-pill styling resurfacing if a slot-less call site is added. -->
		<DisplayChip
			v-for="tag in appliedItems"
			v-if="!hasCustomButton()"
			:key="tag.id"
			:label="tag"
			class="px-2 py-1 ma-0"
			closable
			@click:close="removeChip(tag)" />
	</span>
</template>

<script setup lang="ts">
	import { searchFieldProps, isSearchTooLong as exceedsSearchLimit } from "@/helpers/searchLimit";
	import { ref, computed, watch, onMounted, useSlots } from "vue";
	import { sortTagsBySelected } from "../../helpers/filters";
	import { getRandomColor } from "../../helpers/tags";
	import { isAbortError } from "../../helpers/abort";
	import { logError } from "../../helpers/logError";
	import { useFilterMenu } from "../../composables/useFilterMenu";
	import api from "../../api";
	import { Label } from "../../types/Label";
	import ResponseArray from "../../types/ResponseArray";
	import FilterMenuList from "./shared/FilterMenuList.vue";
	import DisplayChip from "../tags/DisplayChip.vue";

	interface Props {
		preSelectedTags?: string[];
	}

	const props = withDefaults(defineProps<Props>(), {
		preSelectedTags: () => []
	});

	const emit = defineEmits<{
		(e: "filter-tags", tags: Label[]): void;
	}>();

	// LSP-15909: the tag menu previously refetched up to 1000 labels from the API on every open,
	// which is the perceived slowness on mobile. Cache the loaded tags and only refetch once the
	// cache is stale (TTL) so newly-added tags still surface within a reasonable window. The cache
	// can also be invalidated explicitly (e.g. after a tag mutation) via invalidateTagsCache().
	const TAGS_CACHE_TTL_MS = 60_000;

	const searchQuery = ref<string>("");
	const loading = ref<boolean>(false);
	const tags = ref<Label[]>([]);
	const tagsLoadedAt = ref<number>(0);
	// Incremented by invalidateTagsCache() and at the start of every loadTags() call, so any
	// in-flight load can detect it was superseded by a newer load or a cache invalidation and must
	// NOT stamp the cache fresh with its now-stale response (LSP-15909/LSP-16015).
	let tagsLoadGeneration = 0;
	// Count of in-flight label fetches (full loads + targeted reconciles). The spinner/disabled state
	// (LSP-15675) clears only when the LAST one settles, so a superseded/stale request finishing while
	// another is still running can't prematurely re-enable the list, and every fetch gates the UI.
	let inFlightLoadCount = 0;
	async function withTagLoading<T>(operation: () => Promise<T>): Promise<T> {
		inFlightLoadCount++;
		loading.value = true;
		try {
			return await operation();
		} finally {
			inFlightLoadCount--;
			if (inFlightLoadCount === 0) {
				loading.value = false;
			}
		}
	}
	const slots = useSlots();
	let abortController: AbortController | null = null;
	const hasCustomButton = (): boolean => slots.button !== undefined;

	const isTagsCacheFresh = (): boolean => tagsLoadedAt.value > 0 && Date.now() - tagsLoadedAt.value < TAGS_CACHE_TTL_MS;

	const invalidateTagsCache = (): void => {
		tagsLoadedAt.value = 0;
		tagsLoadGeneration++;
	};

	const abortCurrentTagsLoad = (): void => {
		abortController?.abort();
		abortController = null;
	};

	const { isFilterOpen, selectedItems, appliedItems, containerRef, isSelected, toggleSelected, applyFilter, clear } =
		useFilterMenu<Label>(
			// onMenuOpen: reload tags only when the cache is stale (LSP-15909) so newly-added tags
			// still appear without refetching up to 1000 labels on every single open.
			// Note: the composable watcher already restores selectedItems from appliedItems before this runs
			async () => {
				if (!isTagsCacheFresh()) {
					await loadTags();
				} else if (props.preSelectedTags.length > 0) {
					// Cache hit skips loadTags(), which is where preselected tags get reconciled
					// into the list. URL-synced preSelectedTags can include ids not yet in tags.value,
					// so still ensure they're present (cheap — only fetches if one is missing) (LSP-15909).
					// Wrap in withTagLoading so the list is disabled during that targeted fetch too (LSP-15675).
					abortCurrentTagsLoad();
					abortController = new AbortController();
					const menuOpenSignal = abortController.signal;
					await withTagLoading(() => ensurePreselectedTagsInList(menuOpenSignal));
					if (abortController?.signal === menuOpenSignal) abortController = null;
				}
				// If no applied filters exist, check preSelectedTags for initial selection
				if (appliedItems.value.length === 0 && props.preSelectedTags.length > 0) {
					selectedItems.value = tags.value.filter((t) => props.preSelectedTags.includes(t.id));
				}
			},
			// onMenuClose: reset search query
			() => {
				abortCurrentTagsLoad();
				searchQuery.value = "";
			}
		);

	// Computed
	const listTags = computed((): Label[] => {
		if (exceedsSearchLimit(searchQuery.value)) return [];
		return sortTagsBySelected(
			tags.value.filter((tag) => tag.name.toLowerCase().includes(searchQuery.value.toLowerCase())),
			props.preSelectedTags
		);
	});

	const headerText = computed((): string => {
		return "Tags";
	});

	// Methods
	const onMount = async (): Promise<void> => {
		await setPreselectedTags();
		appliedItems.value = [...selectedItems.value];
	};

	const loadTags = async (): Promise<void> => {
		abortCurrentTagsLoad();
		abortController = new AbortController();
		const signal = abortController.signal;
		const generation = ++tagsLoadGeneration;
		// withTagLoading keeps the list disabled until the LAST in-flight fetch settles — a stale load
		// finishing while a newer one is still running must not clear the spinner early (LSP-15909/LSP-15675).
		await withTagLoading(async () => {
			try {
				const response: ResponseArray<Label> = await api.getLabels(
					{
						pageSize: 1000,
						order: [["name", "ASC NULLS LAST", ["ci"]]]
					},
					undefined,
					{ signal }
				);
				// Drop a response that was superseded (cache invalidated, or a newer load started) while this
				// request was in flight — applying it would overwrite fresher tags with stale rows and/or
				// falsely mark the cache fresh (LSP-15909). Guard every tags.value write, not just the stamp.
				if (signal.aborted || generation !== tagsLoadGeneration) {
					return;
				}
				tags.value = response.rows;
				tagsLoadedAt.value = Date.now();

				if (props.preSelectedTags.length > 0) {
					await ensurePreselectedTagsInList(signal);
				}
			} catch (error) {
				if (isAbortError(error)) {
					return;
				}
				// Only clear if this load is still current — a stale failure must not wipe tags a newer load
				// already populated. tagsLoadedAt is only set on success, so a failed (current) load leaves the
				// cache stale and the next open re-attempts the fetch without an explicit invalidate.
				if (!signal.aborted && generation === tagsLoadGeneration) {
					tags.value = [];
					logError("TagFilter: loadTags failed", error, { generation, tagsLoadGeneration });
				}
			} finally {
				if (abortController?.signal === signal) {
					abortController = null;
				}
			}
		});
	};

	const ensurePreselectedTagsInList = async (signal?: AbortSignal): Promise<void> => {
		const generation = tagsLoadGeneration;
		try {
			const tagIds = tags.value.map((c) => c.id);
			if (props.preSelectedTags.some((c) => !tagIds.includes(c))) {
				const preSelectedTagsResult: ResponseArray<Label> = await api.getLabels(
					{
						where: {
							id: { $in: props.preSelectedTags }
						}
					},
					undefined,
					signal ? { signal } : undefined
				);
				// Drop a superseded reconcile (cache invalidated, or a newer load completed) while this
				// request was in flight — otherwise it could overwrite a fresher tag list with stale rows
				// (LSP-15909). Completes the generation guard for every tags.value write in the load path.
				if (signal?.aborted || generation !== tagsLoadGeneration) {
					return;
				}
				const tagMap = new Map<string, Label>();
				[...preSelectedTagsResult.rows, ...tags.value].forEach((tag) => {
					tagMap.set(tag.id, tag);
				});
				tags.value = Array.from(tagMap.values());
			}
		} catch (error) {
			if (isAbortError(error)) {
				return;
			}
			logError("TagFilter: ensurePreselectedTagsInList failed", error, {
				preSelectedTagCount: props.preSelectedTags.length,
				currentTagCount: tags.value.length
			});
		}
	};

	const setPreselectedTags = async (): Promise<void> => {
		await ensurePreselectedTagsInList();
		selectedItems.value = tags.value.filter((t) => props.preSelectedTags.includes(t.id));
	};

	function removeChip(tag: Label): void {
		appliedItems.value = appliedItems.value.filter((applied) => applied.id !== tag.id);
		selectedItems.value = selectedItems.value.filter((selected) => selected.id !== tag.id);
		emit("filter-tags", [...appliedItems.value]);
	}

	const getBackgroundColor = (label: Label): string => {
		if (label.baseColor) {
			return label.baseColor;
		}
		const hexColor = getRandomColor();
		label.baseColor = hexColor;
		return hexColor;
	};

	// Watchers
	watch(
		() => props.preSelectedTags,
		(newVal: string[]) => {
			if (newVal.length > 0) {
				// Abort any in-flight full load so its response can't overwrite tags after we've set selection.
				abortCurrentTagsLoad();
				selectedItems.value = tags.value.filter((t) => newVal.includes(t.id));
				appliedItems.value = [...selectedItems.value];
			} else {
				selectedItems.value = [];
				appliedItems.value = [];
			}
		}
	);

	// Lifecycle
	onMounted(async () => {
		await onMount();
	});

	defineExpose({
		clear: (emitEvent: boolean = false) => clear(() => emit("filter-tags", []), emitEvent),
		invalidateTagsCache,
		isFilterOpen,
		// exposed for tests (mirrors isFilterOpen) — lets specs assert the loaded tag list + loading state
		tags,
		loading
	});
</script>

<style scoped>
	.menu-content {
		width: 100%;
		max-width: 300px;
	}

	.tag-chip {
		max-width: 100%;
	}

	.tag-skeleton-row {
		background: transparent;
	}

	.status-label-filter {
		width: 28px;
		height: 28px;
		font-size: 1rem;
		background-color: #b1d8ff;
		color: #0070ff;
	}

	.status-label-chip {
		font-size: 12px;
		height: 28px;
		box-sizing: border-box;
	}
</style>
